import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/print.css";
import "./i18n"; // Initialize i18n before rendering
import { inject } from "@vercel/analytics";
import { toast } from "sonner";

inject();

// ── PWA install prompt: capture as early as possible ──────────────────
// beforeinstallprompt can fire before any component that reads it (Login,
// the Home Android banner) has mounted — bridge it onto window immediately
// so usePWAInstall() never misses it. usePWAInstall reads/writes this same
// global.
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
});

const hostname = window.location.hostname;
const isPreview =
  hostname.includes("-preview--") ||
  hostname.startsWith("preview--") ||
  hostname.endsWith(".lovableproject.com") ||
  hostname === "lovableproject.com" ||
  hostname.endsWith(".lovableproject-dev.com") ||
  hostname === "lovableproject-dev.com" ||
  hostname.endsWith(".beta.lovable.dev") ||
  hostname === "beta.lovable.dev" ||
  window.self !== window.top;

// ── Chunk / dynamic-import error auto-recovery ──────────────────────
function isChunkError(msg: string | undefined): boolean {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("failed to fetch dynamically imported module") ||
    lower.includes("loading chunk") ||
    lower.includes("loading css chunk") ||
    lower.includes("unable to preload") ||
    lower.includes("error loading dynamically imported module")
  );
}

// Evaluated at call-time (not startup) so navigating to /admin after load is protected
function shouldPreserveInProgressState(): boolean {
  return isPreview || window.location.pathname.startsWith("/admin");
}

async function clearSwCachesAndReload() {
  if (shouldPreserveInProgressState()) {
    console.warn("[Recovery] Automatic reload skipped to preserve in-progress edits.");
    return;
  }

  const RECOVERY_KEY = "__glucofy_chunk_recovery";
  const last = sessionStorage.getItem(RECOVERY_KEY);
  // Prevent infinite reload loops: only once every 30s
  if (last && Date.now() - Number(last) < 30_000) return;
  sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));

  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  if ("serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  // Cache-bust para forçar HTML fresh do servidor (não usa cache HTTP do browser)
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString());
  window.location.replace(url.toString());
}

// Global error handlers — catch chunk errors + log everything
window.addEventListener("error", (e) => {
  console.error("[GlobalError]", e.error || e.message);
  if (isChunkError(e.message) || isChunkError(e.error?.message)) {
    clearSwCachesAndReload();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  console.error("[UnhandledRejection]", e.reason);
  const msg =
    typeof e.reason === "string"
      ? e.reason
      : e.reason?.message || String(e.reason);
  if (isChunkError(msg)) {
    clearSwCachesAndReload();
  }
});

async function clearAllSwCaches() {
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

// ── Graceful "new version available" prompt ──────────────────────────
// registerType: "prompt" in vite.config means the new SW installs but waits
// (skipWaiting: false) until we tell it to take over — this is the UI for that:
// a dismissible toast the user can act on whenever they're ready, instead of
// an unannounced background swap.
function getStoredLang(): "en" | "es" | "de" {
  try {
    const stored = localStorage.getItem("app_language");
    if (stored === "en" || stored === "es" || stored === "de") return stored;
  } catch { /* noop */ }
  return "en";
}

const UPDATE_TOAST_TEXT: Record<"en" | "es" | "de", { message: string; action: string }> = {
  en: { message: "New version available — update now?", action: "Update" },
  es: { message: "Nueva versión disponible — ¿actualizar ahora?", action: "Actualizar" },
  de: { message: "Neue Version verfügbar — jetzt aktualisieren?", action: "Aktualisieren" },
};

function promptSwUpdate(waitingWorker: ServiceWorker) {
  const { message, action } = UPDATE_TOAST_TEXT[getStoredLang()];
  toast(message, {
    duration: Infinity,
    action: {
      label: action,
      onClick: () => waitingWorker.postMessage({ type: "SKIP_WAITING" }),
    },
  });
}

function watchForSwUpdates(registration: ServiceWorkerRegistration) {
  if (registration.waiting) promptSwUpdate(registration.waiting);

  registration.addEventListener("updatefound", () => {
    const newWorker = registration.installing;
    if (!newWorker) return;
    newWorker.addEventListener("statechange", () => {
      // installed + an already-active worker means this is an update, not the first install
      if (newWorker.state === "installed" && registration.active) {
        promptSwUpdate(newWorker);
      }
    });
  });
}

if ("serviceWorker" in navigator) {
  let reloadedForUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadedForUpdate) return;
    reloadedForUpdate = true;
    window.location.reload();
  });
}

// ── Service Worker lifecycle ────────────────────────────────────────
if ("serviceWorker" in navigator) {
  if (isPreview) {
    // In preview: never force reload/replace. Just detach old SW state silently.
    navigator.serviceWorker
      .getRegistrations()
      .then(async (regs) => {
        if (regs.length > 0) {
          await Promise.allSettled(regs.map((reg) => reg.unregister()));
        }
        await clearAllSwCaches();
      })
      .catch(() => {});
  } else {
    // Production only: register the generated Service Worker manually.
    // injectRegister is disabled in vite.config so this is the single
    // registration point, ensuring preview hosts never get an SW.
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => watchForSwUpdates(registration))
        .catch(() => {});
    });
  }
}

// Remove o parâmetro de cache-bust da URL sem recarregar a página
if (window.location.search.includes("_r=")) {
  const url = new URL(window.location.href);
  url.searchParams.delete("_r");
  history.replaceState(null, "", url.toString());
}

createRoot(document.getElementById("root")!).render(<App />);
